// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// One tier decision for the whole client (M3-35, M3-37).
//
// The renderer is sized by tier and so is the audio voice pool, and the two are built minutes
// apart — the machine when a route mounts, the audio on the first gesture, which may be a tap on
// a landing page long before any of it. Two decisions would be two probes and, on a device near a
// threshold, two different answers: a machine rendering at `medium` with a `high` voice pool is a
// combination nothing ever chose.
//
// Lazily, not at import. A `TierChoice` probes WebGL in its constructor, and a module that did
// that at import time would open a GL context in every test that touched anything downstream of
// it — and in a headless one, get `low` and cache it.

import { TierChoice } from '../world/tierChoice'
import { RENDER_TIERS, type RenderTier } from '../world/tiers'

let cached: TierChoice | undefined

/**
 * `?tier=low`, for looking at what a slower device sees.
 *
 * Read as a real **override** rather than as a temporary view, so it survives the reload — the
 * alternative is a query string that has to be re-typed after every hot module reload, and a
 * setting that behaves differently from the one in the settings panel.
 */
function forcedTier(): RenderTier | undefined {
  const search = (globalThis as { location?: { search?: string } }).location?.search
  if (search === undefined || search === '') return undefined
  const asked = new URLSearchParams(search).get('tier')
  if (asked === null) return undefined
  return (RENDER_TIERS as readonly string[]).includes(asked) ? (asked as RenderTier) : undefined
}

/** The client's tier decision. Probed once, on the first call. */
export function renderTier(): TierChoice {
  if (cached === undefined) {
    const forced = forcedTier()
    cached = new TierChoice(forced === undefined ? {} : { forced })
  }
  return cached
}
